import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminAuditEntry } from 'shared'
import {
  Alert,
  EmptyState,
  SectionSpinner,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRoot,
  TableRow,
  TremorTable,
} from '../ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDate } from '../../lib/dates'

interface AuditLogProps {
  /** Cambia a ogni azione dell'admin: rilegge il registro invece di mostrarne uno vecchio. */
  version: number
}

/** Ruolo e stato portano il prima e il dopo; del nome si sa solo che è cambiato. */
type Change = { from: string; to: string } | true

/** Ruolo e stato si mostrano nella lingua dell'interfaccia; il nome resta com'è. */
function useDescribe() {
  const { t } = useTranslation()

  const label = (field: string, value: string) => {
    if (field === 'role') return t(`admin.role_${value}`)
    if (field === 'status') return t(`admin.status${value.charAt(0).toUpperCase()}${value.slice(1)}`)
    return value
  }

  return (entry: AdminAuditEntry): string => {
    const detail = entry.detail ?? {}

    if (entry.action === 'user_created') {
      const role = typeof detail.role === 'string' ? detail.role : null
      return role ? `${t('admin.role')}: ${label('role', role)}` : ''
    }

    if (entry.action === 'user_updated') {
      return Object.entries(detail as Record<string, Change>)
        .map(([field, change]) => {
          if (change === true) return t('admin.auditNameChanged')
          if (!change || typeof change.from !== 'string') return null

          return t('admin.auditChange', {
            field: t(`admin.auditField_${field}`, { defaultValue: field }),
            from: label(field, change.from),
            to: label(field, change.to),
          })
        })
        .filter(Boolean)
        .join(' · ')
    }

    return ''
  }
}

/**
 * Chi ha fatto cosa a chi, tra gli amministratori.
 *
 * È ciò che rende difendibile il potere di disattivare o modificare un account.
 * Il registro non porta mai credenziali né link: descrive cosa è cambiato, non
 * il segreto che ha reso possibile il cambio.
 */
export default function AuditLog({ version }: AuditLogProps) {
  const { t, i18n } = useTranslation()
  const describe = useDescribe()

  const [entries, setEntries] = useState<AdminAuditEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ data: AdminAuditEntry[] }>('/api/admin/audit-log')
      .then((response) => setEntries(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? t('errors.generic')))
  }, [version, t])

  if (error) return <Alert>{error}</Alert>
  if (!entries) return <SectionSpinner />

  if (entries.length === 0) {
    return <EmptyState title={t('admin.auditEmpty')} description={t('admin.auditEmptyHelp')} />
  }

  // Una persona anonimizzata compare col nome che ha adesso: il registro non
  // conserva quello di prima, ed è il punto dell'anonimizzazione.
  const who = (person: { name: string | null }) => person.name ?? t('admin.auditUnknown')

  return (
    <TableRoot className="border-t border-gray-200 dark:border-gray-800">
      <TremorTable>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{t('common.createdAt')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.auditActor')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.auditAction')}</TableHeaderCell>
            <TableHeaderCell>{t('admin.auditTarget')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((entry) => (
            <TableRow key={entry.id}>
              <TableCell className="whitespace-nowrap">
                {formatDate(entry.created_at, i18n.language)}
              </TableCell>
              <TableCell>{who(entry.actor)}</TableCell>
              <TableCell>
                <div>{t(`admin.audit_${entry.action}`)}</div>
                {describe(entry) && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">{describe(entry)}</div>
                )}
              </TableCell>
              <TableCell>{who(entry.target)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </TremorTable>
    </TableRoot>
  )
}
